import { Button, Text } from '@change/ds-core-ui/next';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core/react/i18n';
// eslint-disable-next-line internal-rules/no-restricted-design-system-imports
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import { ResponseListCard } from './components/ResponseListCard';
import { useResponseListRedesign } from './hooks/useResponseListRedesign';

export function ResponseListRedesign() {
	const {
		data: { displayName, responses, hasNextPage, nextPageLoading },
		actions: { loadMore },
	} = useResponseListRedesign();

	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	const { translate } = useI18n();

	if (!responses.length) return null;

	return (
		<div data-qa="dm-responses-content" ref={containerRef}>
			<Text as="h3" variant="heading" className="mb-6">
				Responses to petitions from {displayName}
			</Text>

			<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
				{responses.map((response, index) =>
					response.petition ? (
						<div key={response.id}>
							{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
								<div className="sr-only" role="alert">
									<TranslatePlural
										value="corgi.decision_maker_profile.responses_list.fetched_more"
										count={responses.length - lastEvent.previousItemsCount}
										replacements={{ count: responses.length - lastEvent.previousItemsCount }}
									/>
								</div>
							)}

							<ResponseListCard
								responseId={response.id}
								message={response.message}
								petitionHref={`/p/${encodeURIComponent(response.petition.slug)}`}
								petitionAsk={response.petition.ask}
								petitionId={response.petition.id}
								ref={getListItemRef(index)}
							/>
						</div>
					) : null,
				)}
			</div>

			{nextPageLoading && (
				<div className="mt-16">
					<div className="sr-only" role="alert">
						<Translate value="corgi.decision_maker_profile.responses_list.fetching_more" />
					</div>
					<Loader size={44} mx="auto" ref={loadingRef} data-testid="load-more-btn" />
				</div>
			)}

			{hasNextPage && !nextPageLoading && (
				<div className="flex justify-center">
					<Button
						className="mt-16 basis-full md:basis-1/3"
						size="small"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: responses.length });
							loadMore();
						}}
						data-qa="dm-responses-load-more"
						ref={loadMoreRef}
					>
						{translate('fe.components.load_more.load_more')}
					</Button>
				</div>
			)}
		</div>
	);
}
